import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ChevronDown, History, Plus, Trash2 } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import {
  blogPromptsAdoptDefault, blogPromptsCreateOverride, blogPromptsDelete, blogPromptsDiff, blogPromptsLint,
  blogPromptsRestore, blogPromptsUpdate, blogPromptsVersions,
} from '../../../services/api';
import { errMsg, fmtDate, useDebouncedCallback } from '../generator/util';

const STATUS = {
  following_default: ['secondary', 'Following the shipped default', 'This row has never been customised, so the current code default is used automatically. Nothing to do after a deploy.'],
  up_to_date: ['success', 'Up to date', 'Identical to the shipped default.'],
  customised: ['info', 'Customised', 'Your edited version is used. It was forked from the current shipped default.'],
  outdated: ['warning', 'Outdated vs shipped default', 'Your edited version is used, but the shipped default has changed since you forked it. View the diff and adopt or merge.'],
};

const KIND_ORDER = ['extract', 'write_blog', 'section', 'verify'];

function highlight(text) {
  return text.split(/(\{[A-Za-z_][A-Za-z0-9_]*\})/).map((part, i) =>
    /^\{[A-Za-z_]/.test(part) ? <mark key={i} className="rounded bg-brand-purple1/15 px-0.5 text-brand-purple1">{part}</mark> : part
  );
}

function VariablePalette({ vars, onInsert }) {
  const [showStatic, setShowStatic] = useState(false);
  const list = vars.filter((v) => showStatic || !v.static);
  return (
    <div className="space-y-1">
      <p className="text-[11px] uppercase text-muted-foreground">Variables (click to insert)</p>
      {list.map((v) => (
        <TooltipProvider key={v.name}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" onClick={() => onInsert(`{${v.name}}`)} className="flex w-full items-start gap-2 rounded px-2 py-1 text-left text-token-base hover:bg-muted">
                <code className="shrink-0 text-brand-purple1">{`{${v.name}}`}</code>
                <span className="min-w-0">
                  <span className="font-medium">{v.label}</span>{v.required && <span className="text-destructive"> *</span>}
                  <span className="block truncate text-[11px] text-muted-foreground">{v.description}</span>
                </span>
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              <p>{v.description}</p>
              {v.example && <pre className="mt-1 whitespace-pre-wrap text-[11px] opacity-80">e.g. {v.example.slice(0, 200)}</pre>}
              {v.aliases?.length > 0 && <p className="mt-1 text-[11px]">Old names: {v.aliases.map((a) => `{${a}}`).join(', ')}</p>}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ))}
      {vars.some((v) => v.static) && (
        <Button variant="link" size="sm" className="h-6 px-2 text-[11px]" onClick={() => setShowStatic(!showStatic)}>
          {showStatic ? 'Hide' : 'Show'} always-the-same variables
        </Button>
      )}
    </div>
  );
}

function TemplateEditor({ t, def, vars, onChanged }) {
  const [content, setContent] = useState(t.content);
  const [note, setNote] = useState('');
  const [lint, setLint] = useState({ lint: t.lint, rendered: null });
  const [saving, setSaving] = useState(false);
  const [diff, setDiff] = useState(null);
  const [versions, setVersions] = useState(null);
  const [showSystem, setShowSystem] = useState(false);
  const [showRender, setShowRender] = useState(false);
  const ta = useRef(null);
  const st = STATUS[t.status] || STATUS.customised;
  const dirty = content !== t.content;

  useEffect(() => { setContent(t.content); setLint({ lint: t.lint, rendered: null }); }, [t.id, t.content, t.lint]);

  const runLint = useDebouncedCallback(async (c) => {
    try { setLint(await blogPromptsLint(t.kind, c)); } catch (e) { setLint({ lint: { errors: [errMsg(e)], warnings: [] } }); }
  }, 600);

  const insert = (token) => {
    const el = ta.current;
    const s = el?.selectionStart ?? content.length;
    const e = el?.selectionEnd ?? content.length;
    const next = content.slice(0, s) + token + content.slice(e);
    setContent(next);
    runLint(next);
    setTimeout(() => { el?.focus(); el?.setSelectionRange(s + token.length, s + token.length); }, 0);
  };

  const save = async () => {
    setSaving(true);
    try {
      const saved = await blogPromptsUpdate({ id: t.id, content, change_note: note || null });
      onChanged(saved);
      setNote('');
      toast.success(`Saved v${saved.version}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };
  const adopt = async () => {
    try { onChanged(await blogPromptsAdoptDefault(t.id)); setDiff(null); toast.success('Shipped default adopted'); } catch (e) { toast.error(errMsg(e)); }
  };
  const loadDiff = async () => { try { setDiff(await blogPromptsDiff(t.id, 'default')); } catch (e) { toast.error(errMsg(e)); } };
  const loadVersions = async () => { try { setVersions(await blogPromptsVersions(t.id)); } catch (e) { toast.error(errMsg(e)); } };
  const restore = async (v) => {
    try { onChanged(await blogPromptsRestore(t.id, v.id)); setVersions(null); toast.success(`Restored v${v.version}`); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => {
    if (!window.confirm('Remove this per-type override? The kind-wide template will be used for this blog type.')) return;
    try { await blogPromptsDelete(t.id); onChanged(null, t.id); } catch (e) { toast.error(errMsg(e)); }
  };

  const errors = lint?.lint?.errors || [];
  const warnings = lint?.lint?.warnings || [];

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <TooltipProvider><Tooltip><TooltipTrigger asChild><span><Badge variant={st[0]}>{st[1]}</Badge></span></TooltipTrigger><TooltipContent className="max-w-xs">{st[2]}</TooltipContent></Tooltip></TooltipProvider>
          <Badge variant="outline">v{t.version}</Badge>
          {t.status === 'following_default' && t.content !== def.user && (
            <span className="text-[11px] text-muted-foreground">(the stored text is an older version — editing starts from the current default)</span>
          )}
          <span className="flex-1" />
          {t.status !== 'following_default' && t.status !== 'up_to_date' && (
            <Button size="sm" variant="outline" onClick={loadDiff}>View diff vs shipped default</Button>
          )}
          <Button size="sm" variant="ghost" onClick={loadVersions}><History /> History</Button>
          {t.blog_type_id && <Button size="sm" variant="ghost" onClick={del}><Trash2 /> Remove override</Button>}
        </div>

        <Collapsible open={showSystem} onOpenChange={setShowSystem}>
          <CollapsibleTrigger className="flex items-center gap-1 text-token-base text-muted-foreground"><ChevronDown size={14} className={showSystem ? 'rotate-180' : ''} /> Locked system prompt (rules that always apply — edit in code)</CollapsibleTrigger>
          <CollapsibleContent><pre className="mt-1 whitespace-pre-wrap rounded bg-muted/40 p-2 font-mono text-[11px]">{def.system}</pre></CollapsibleContent>
        </Collapsible>

        <div className="relative">
          <Textarea ref={ta} rows={22} value={t.status === 'following_default' && !dirty ? def.user : content}
            onFocus={() => { if (t.status === 'following_default' && !dirty) setContent(def.user); }}
            onChange={(e) => { setContent(e.target.value); runLint(e.target.value); }}
            className="font-mono text-[12px] leading-5" />
        </div>
        {errors.length > 0 && <div className="rounded border border-destructive/40 bg-destructive/5 p-2 text-token-base text-destructive">{errors.map((e, i) => <div key={i}>• {e}</div>)}</div>}
        {warnings.length > 0 && <div className="rounded border border-warning/40 bg-warning/5 p-2 text-token-base text-warning">{warnings.map((e, i) => <div key={i}>• {e}</div>)}</div>}
        <div className="flex flex-wrap items-center gap-2">
          <Input placeholder="What changed? (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="h-8 max-w-sm" />
          <Button size="sm" disabled={!dirty || errors.length > 0 || saving} onClick={save}>Save</Button>
          {dirty && <Button size="sm" variant="ghost" onClick={() => { setContent(t.content); setLint({ lint: t.lint }); }}>Discard</Button>}
          <Button size="sm" variant="ghost" onClick={() => { setShowRender(true); if (!lint?.rendered) runLint(content); }}>Preview with example data</Button>
        </div>
      </div>
      <VariablePalette vars={vars} onInsert={insert} />

      <Dialog open={!!diff} onOpenChange={(o) => !o && setDiff(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[860px]">
          <DialogHeader><DialogTitle>Your version vs the shipped default</DialogTitle></DialogHeader>
          <pre className="whitespace-pre-wrap rounded bg-muted/40 p-3 font-mono text-[11px]">
            {(diff?.diff || '(identical)').split('\n').map((l, i) => (
              <div key={i} className={l.startsWith('+') ? 'text-success' : l.startsWith('-') ? 'text-destructive' : ''}>{l}</div>
            ))}
          </pre>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiff(null)}>Keep mine</Button>
            <Button onClick={adopt}>Adopt shipped default</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!versions} onOpenChange={(o) => !o && setVersions(null)}>
        <DialogContent className="max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>History</DialogTitle></DialogHeader>
          {(versions || []).length === 0 && <p className="text-muted-foreground">No saved versions yet.</p>}
          {(versions || []).map((v) => (
            <div key={v.id} className="flex items-center justify-between rounded border border-borderGrey px-2 py-1.5 text-token-base">
              <span>v{v.version} · {fmtDate(v.created_at)} {v.change_note ? `· ${v.change_note}` : ''}</span>
              <Button size="sm" variant="outline" className="h-7" onClick={() => restore(v)}>Restore</Button>
            </div>
          ))}
        </DialogContent>
      </Dialog>

      <Dialog open={showRender} onOpenChange={setShowRender}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[800px]">
          <DialogHeader><DialogTitle>Rendered with example values</DialogTitle></DialogHeader>
          <pre className="whitespace-pre-wrap rounded bg-muted/40 p-3 font-mono text-[12px]">{lint?.rendered ? highlight(lint.rendered) : 'Rendering…'}</pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function TemplatesTab({ data, onChanged, reload }) {
  const [kind, setKind] = useState('write_blog');
  const [typeForOverride, setTypeForOverride] = useState('');
  const templates = (data.templates || []).filter((t) => t.kind === kind);
  const def = data.defaults?.[kind];
  const vars = data.variables?.[kind] || [];

  const createOverride = async () => {
    try { await blogPromptsCreateOverride(kind, parseInt(typeForOverride, 10)); setTypeForOverride(''); reload(); toast.success('Override created'); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {KIND_ORDER.filter((k) => data.defaults?.[k]).map((k) => (
          <Button key={k} size="sm" variant={k === kind ? 'default' : 'outline'} onClick={() => setKind(k)}>{data.defaults[k].label}</Button>
        ))}
      </div>
      {def && <p className="text-token-base text-muted-foreground">{def.purpose} · model {def.model} · effort {def.effort}</p>}

      {templates.map((t) => (
        <div key={t.id} className="rounded-token-large border border-borderGrey bg-white p-4">
          <p className="mb-2 text-token-md font-medium">{t.name}{t.blog_type_id ? '' : ' (all blog types)'}</p>
          <TemplateEditor t={t} def={def} vars={vars} onChanged={onChanged} />
        </div>
      ))}

      <div className="flex items-center gap-2">
        <Label className="text-token-base">Different template for one blog type:</Label>
        <Select value={typeForOverride || '__none'} onValueChange={(v) => setTypeForOverride(v === '__none' ? '' : v)}>
          <SelectTrigger className="h-8 w-[220px]"><SelectValue placeholder="Choose a type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none">Choose a type</SelectItem>
            {(data.blog_types || []).filter((bt) => !templates.some((t) => t.blog_type_id === bt.id)).map((bt) => <SelectItem key={bt.id} value={String(bt.id)}>{bt.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" disabled={!typeForOverride} onClick={createOverride}><Plus /> Create override</Button>
      </div>
    </div>
  );
}
